package org.zest.insan_360_website.data.page_states
import org.zest.insan_360_website.data.localized

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.browser.api
import com.varabyte.kobweb.browser.storage.getItem
import com.varabyte.kobweb.browser.storage.setItem
import kotlinx.browser.localStorage
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import org.zest.insan_360_website.components.widgets.ToastState
import org.zest.insan_360_website.components.widgets.ToastType
import org.zest.insan_360_website.data.Language
import org.zest.insan_360_website.data.USER_LANGUAGE_KEY
import org.zest.insan_360_website.model.ApiResponse
import org.zest.insan_360_website.model.CreditSessionPayload
import org.zest.insan_360_website.model.CreditStatus
import org.zest.insan_360_website.utils.NetworkUtils
import org.zest.insan_360_website.utils.processResponse

object CreditState {
    var creditState by mutableStateOf<CreditStatus?>(null)
    var activeCreditSession by mutableStateOf(CreditSessionPayload(false))
    var isLoading by mutableStateOf(true)
    var isLoadingSession by mutableStateOf(false)
    var isSessionExpired by mutableStateOf(false)
    var needUpdate by mutableStateOf(false)

    fun reset() {
        creditState = null
        activeCreditSession = CreditSessionPayload(false)
        isLoading = true
        isLoadingSession = false
        isSessionExpired = false
        needUpdate = false
    }

    suspend fun fetchCurrentCreditStatus() {
        val email = AuthState.email ?: return
        val token = AuthState.token ?: return
        isLoading = true

        try {
            val headers = mapOf("Authorization" to "Bearer $token")
            val responseBytes = NetworkUtils.runSecureAction {
                window.api.tryGetBytes(
                    "credit/status?email=$email",
                    headers = headers
                )
            }
            if (responseBytes != null) {
                val responseText = responseBytes.decodeToString()
                val result = Json.decodeFromString<ApiResponse<String>>(responseText)
                if (result.success) {
                    val statusString = result.data
                    if (statusString.isNullOrBlank()) {
                        console.warn("No credit link to user's account.")
                    } else {
                        creditState = try {
                            CreditStatus.valueOf(statusString)
                        } catch (_: Exception) {
                            null
                        }
                        return
                    }
                }
            }
            creditState = null
        } catch (e: Exception) {
            console.error("Failed to fetch credit status: ${e.message}")
            creditState = null
        } finally {
            isLoading = false
        }
    }

    suspend fun fetchActiveCreditSession() {
        isLoadingSession = true

        try {
            val response = NetworkUtils.patchSecureApi("/api/credit/fetch-session")
                ?: throw Exception(org.zest.insan_360_website.data.LanguageSettings.ResponseError.session_expired.localized)

            val sessionData = processResponse<CreditSessionPayload>(response, fallbackErrorLog = "Error fetching credit session.")
            activeCreditSession = sessionData ?: CreditSessionPayload(false)

        } catch (e: Exception) {
            console.error("Failed to fetch credit session: ${e.message}")
        } finally {
            isLoadingSession = false
        }
    }

    fun handleExtendCreditSession(
        scope: CoroutineScope, 
        toast: ToastState, 
        onComplete: () -> Unit = {},
        onSuccess: () -> Unit
    ) {
        scope.launch {
            try {
                val response = NetworkUtils.patchSecureApi("/api/credit/extend-session")
                    ?: throw Exception(org.zest.insan_360_website.data.LanguageSettings.ResponseError.session_expired.localized)

                processResponse<Unit>(response, toast = toast, fallbackErrorLog = "Error extending credit session.") {
                    isSessionExpired = false
                    onSuccess()
                }

            } catch (e: Throwable) {
                val errorMsg = e.message ?: "Failed to extend credit session: Network error."
                toast.show(errorMsg, ToastType.ERROR)
                console.error(errorMsg)
            } finally {
                onComplete()
            }
        }
    }
}

object UserPreferences {
    var language by mutableStateOf(Language.BM)

    fun loadPreferences() {
        val saved = localStorage.getItem(USER_LANGUAGE_KEY)
        language = saved ?: Language.BM
    }

    fun setLanguage(newLanguage: Language) {
        language = newLanguage
        localStorage.setItem(USER_LANGUAGE_KEY, newLanguage)
    }
}
